import React from "react";
import PropTypes from "prop-types";
import { Info } from "lucide-react";
import { cn } from "../../lib/utils";
import { LightTooltip } from "../ui/tooltip";

/**
 * It makes no decisions of its own: every value (labels, colors, the text under
 * each bar) is provided by the caller. It only lays the segments out in an
 * equal-width row and paints each one with the values it is handed.
 *
 * Each step:
 *   - statusLabel: the label text, rendered verbatim (include any suffix yourself).
 *   - date / time: the two secondary strings under the label (a dot separates
 *     them only when both are present). Put whatever you want here.
 *   - color: the bar background color.
 *   - textColor: the color for the label and secondary line.
 *   - status: optional; only used to set aria-current="step" when it is "current".
 *   - info: optional; when present, an info icon is shown next to the label and
 *     reveals this text on hover/focus (e.g. the stage's descriptive subtitle).
 */
const StatusStepper = ({ steps, className, ariaLabel }) => {
  if (!Array.isArray(steps) || steps.length === 0) return null;

  return (
    <ol
      aria-label={ariaLabel}
      className={cn("flex w-full items-start gap-[12px]", className)}
    >
      {steps.map((step, index) => {
        const textStyle = step.textColor ? { color: step.textColor } : undefined;
        const hasSecondary = step.date || step.time;

        const labelSpan = (
          <span
            className="text-[12px] font-medium leading-[18px]"
            style={textStyle}
          >
            {step.statusLabel}
          </span>
        );

        return (
          <li
            key={`${step.statusLabel}-${index}`}
            aria-current={step.status === "current" ? "step" : undefined}
            className="flex min-w-0 flex-1 flex-col gap-2"
          >
            <span
              aria-hidden="true"
              className="h-2 w-full rounded-full"
              style={step.color ? { backgroundColor: step.color } : undefined}
            />

            <div className="flex flex-col gap-[6px]">
              {step.info ? (
                <div className="flex items-center gap-1">
                  {labelSpan}
                  <LightTooltip title={step.info} side="top">
                    <span
                      role="button"
                      tabIndex={0}
                      aria-label={`${step.statusLabel} details`}
                      className="inline-flex shrink-0 cursor-help"
                      style={textStyle}
                    >
                      <Info className="h-3.5 w-3.5" />
                    </span>
                  </LightTooltip>
                </div>
              ) : (
                labelSpan
              )}

              {hasSecondary && (
                <div
                  className="flex items-center gap-[6px] text-[12px] leading-[18px]"
                  style={textStyle}
                >
                  {step.date && <span>{step.date}</span>}
                  {step.date && step.time && (
                    <span
                      aria-hidden="true"
                      className="h-1 w-1 shrink-0 rounded-full bg-current"
                    />
                  )}
                  {step.time && <span>{step.time}</span>}
                </div>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
};

StatusStepper.propTypes = {
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      statusLabel: PropTypes.string.isRequired,
      date: PropTypes.string,
      time: PropTypes.string,
      color: PropTypes.string,
      textColor: PropTypes.string,
      status: PropTypes.string,
      info: PropTypes.string,
    })
  ).isRequired,
  className: PropTypes.string,
  ariaLabel: PropTypes.string,
};

export { StatusStepper };
